import type { JobSalary, MetroArea, Quantile, WatchlistDto } from "@lossdog/job-watchlists";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";

const METRO_AREA_DEFAULT = "16980";
const QUANTILE_DEFAULT = "a_median";

interface UseJobSalariesOptions {
  defaultToAll?: boolean;
  autoSelectDefaults?: boolean;
}

interface UseJobSalariesReturn {
  // Data
  jobSalaries: JobSalary[] | undefined;

  // Available options
  watchlistGroups: any[] | undefined;
  watchlists: WatchlistDto[];
  metroAreas: MetroArea[] | undefined;
  quantiles: Quantile[] | undefined;

  // Selected filters
  selectedWatchlistGroup: { id: string; title: string } | null;
  selectedWatchlist: { id: string; title: string } | null;
  selectedMetroArea: MetroArea | null;
  selectedQuantile: Quantile | null;

  // Actions
  setSelectedWatchlistGroup: (group: { id: string; title: string } | null) => void;
  setSelectedWatchlist: (watchlist: { id: string; title: string } | null) => void;
  setSelectedMetroArea: (metroArea: MetroArea | null) => void;
  setSelectedQuantile: (quantile: Quantile | null) => void;

  // Loading state
  isLoading: boolean;
}

export const useJobSalaries = (options: UseJobSalariesOptions = {}): UseJobSalariesReturn => {
  const { defaultToAll = false, autoSelectDefaults = true } = options;

  const { client, isLoading } = useJobSalaryContext();

  // Filter state
  const [selectedWatchlistGroup, setSelectedWatchlistGroup] = useState<{ id: string; title: string } | null>(null);
  const [selectedWatchlist, setSelectedWatchlist] = useState<{ id: string; title: string } | null>(null);
  const [selectedMetroArea, setSelectedMetroArea] = useState<MetroArea | null>(null);
  const [selectedQuantile, setSelectedQuantile] = useState<Quantile | null>(null);

  // Data state
  const [watchlists, setWatchlists] = useState<WatchlistDto[]>([]);
  const [jobSalaries, setJobSalaries] = useState<JobSalary[] | undefined>(undefined);

  // Memoized computed values from client
  const watchlistGroups = useMemo(() => client?.getWatchlistGroups(), [client]);
  const metroAreas = useMemo(() => client?.getMetroAreas(), [client]);
  const quantiles = useMemo(() => client?.getQuantiles(), [client]);

  // Fetch job salaries when selections change
  const fetchJobSalaries = useCallback(() => {
    if (isLoading || !client || !selectedWatchlist || !selectedMetroArea || !selectedQuantile) {
      setJobSalaries(undefined);
      return;
    }

    try {
      const result = client.getJobSalaries(selectedWatchlist.id, selectedMetroArea.id, selectedQuantile.id);
      setJobSalaries(result?.jobSalaries);
    } catch {
      setJobSalaries(undefined);
    }
  }, [client, isLoading, selectedWatchlist, selectedMetroArea, selectedQuantile]);

  // Set default selections when data is available
  useEffect(() => {
    if (isLoading || !autoSelectDefaults) return;

    if (watchlistGroups?.length && !selectedWatchlistGroup) {
      if (defaultToAll) {
        const allGroup = watchlistGroups.find((group) => group.id === "All");
        if (allGroup) {
          setSelectedWatchlistGroup(allGroup);
        }
      } else {
        setSelectedWatchlistGroup(watchlistGroups[0]);
      }
    }

    if (selectedWatchlistGroup) {
      const nextWatchlists = watchlistGroups?.find((group) => group.id === selectedWatchlistGroup.id)?.watchlists ?? [];
      setWatchlists(nextWatchlists);

      const isCurrentWatchlistInNewGroup =
        selectedWatchlist && nextWatchlists.some((wl) => wl.id === selectedWatchlist.id);

      if (nextWatchlists.length > 0 && (!selectedWatchlist || !isCurrentWatchlistInNewGroup)) {
        setSelectedWatchlist(nextWatchlists[0]);
      }
    }

    if (metroAreas?.length && !selectedMetroArea) {
      setSelectedMetroArea(metroAreas?.find((metroArea) => metroArea.id === METRO_AREA_DEFAULT) ?? metroAreas?.[0]);
    }

    if (quantiles?.length && !selectedQuantile) {
      setSelectedQuantile(quantiles?.find((quantile) => quantile.id === QUANTILE_DEFAULT) ?? quantiles?.[0]);
    }
  }, [
    watchlistGroups,
    metroAreas,
    quantiles,
    selectedWatchlistGroup,
    selectedMetroArea,
    selectedQuantile,
    isLoading,
    autoSelectDefaults,
    defaultToAll,
    selectedWatchlist,
  ]);

  // Fetch job salaries when selections change
  useEffect(() => {
    fetchJobSalaries();
  }, [fetchJobSalaries]);

  return {
    // Data
    jobSalaries,

    // Available options
    watchlistGroups,
    watchlists,
    metroAreas,
    quantiles,

    // Selected filters
    selectedWatchlistGroup,
    selectedWatchlist,
    selectedMetroArea,
    selectedQuantile,

    // Actions
    setSelectedWatchlistGroup,
    setSelectedWatchlist,
    setSelectedMetroArea,
    setSelectedQuantile,

    // Loading state
    isLoading,
  };
};
